import { Component, OnInit, Input, ElementRef, Renderer2, SimpleChanges, OnChanges } from '@angular/core';
import { ConfigService } from 'src/app/common/config/config.service';
import { UiAvatar } from './ui-avatar.models';

// how to use
// <ui-avatar [avatar]="url" [name]="profileName" shape="squircle" bg="warning-5" color="warning-20"></ui-avatar>
// To update [attr.xlink:href] to [attr.href] when we no longer support safari 11.1

@Component({
    selector: 'ui-avatar',
    template:
        `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 128 128" *ngIf="!avatar && !name" [style.height]="iconSize">
            <use [attr.xlink:href]="configService.config.pathIcons.concat(type, '/', icon, '.svg#', icon)"/>
        </svg>
        <figure class="img-responsive ratio-1by1" [ngClass]="name || avatar ? 'ready' : 'loading'">
            <figcaption *ngIf="name && name.trim() !== '' " [style.fontSize]="fontSize">{{computedName}}</figcaption>
            <img *ngIf="avatar && avatar.trim() !== '' " [src]="avatar"
                (load)="onImgLoad($event)" (error)="onImgError($event)" [style.height]="landscapeImg ? '100%' : 'auto'"
                [style.width]="landscapeImg ? 'auto' : '100%'">
        </figure>
        <ng-content></ng-content>
    `,
    styleUrls: ['./ui-avatar.component.scss'],
    host: {
        '[class.ui-avatar]': 'true',
        '[class.animated]': 'animated'
    }
})
export class UiAvatarComponent implements OnInit, OnChanges, UiAvatar.Component {

    // content input - optional - if none will be provided default genric/user_profile will be shown)
    @Input() avatar: string;
    @Input() name: string;
    @Input() initials = UiAvatar.defaultProp.initials;

    // design inputs - optional
    @Input() shape = UiAvatar.defaultProp.shape;
    @Input() size = UiAvatar.defaultProp.size;
    @Input() bg: string;
    @Input() iconSize = UiAvatar.defaultProp.iconSize;
    @Input() color = UiAvatar.defaultProp.color;
    @Input() type = 'general';
    @Input() icon = UiAvatar.defaultProp.icon;
    @Input() fontSize = UiAvatar.defaultProp.fontSize;
    @Input() animated = UiAvatar.defaultProp.animated;

    private computedName = '';
    public landscapeImg = false;

    constructor(
        readonly configService: ConfigService,
        readonly el: ElementRef,
        readonly renderer: Renderer2) {
    }

    ngOnInit() {
        this.el.nativeElement.style.height = this.size + 'px';
        this.el.nativeElement.style.width = this.size + 'px';

        if (this.shape) {
            this.renderer.addClass(this.el.nativeElement, this.shape);
        }

        if (this.bg) {
            this.renderer.addClass(this.el.nativeElement, 'bg-' + this.bg);
        }

        this.renderer.addClass(this.el.nativeElement, 'color-' + this.color);
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.name && changes.name.currentValue !== changes.name.previousValue) {
            this.computedName = '';
            this.computeName(this.initials);
        }
    }

    /**
    * This sets the avatar initials based on user's name
    * @param {number} initials Number of desired initials to be displayed
    * @returns {nothing} Sets variable
    */
    computeName(initials){
        if (this.name) {
            const nameArr = this.name.trim().split(" ");

            for (const word of nameArr) {
                this.computedName += word.substr(0,1).toUpperCase();
                if (nameArr.indexOf(word) === initials - 1) {
                    break;
                }
            }
        }
    }

    /**
    * This sets landscapeImg variable, verifies if image is landscape or portrait
    * @param {event} event window.event is deprecated so it can't be auto implied anymore, it must be specifically passed
    * @returns {nothing} Sets variables
    */
    onImgLoad(event){
        this.landscapeImg = event.currentTarget.width >= event.currentTarget.height;
    }

    /**
    * This removes img tag from dom, if path is incorect
    * @param {event} event
    * @returns {nothing} Sets variables
    */
    onImgError(event){
        event.currentTarget.remove();
    }
}
